import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { PRIORITY, scoreColor } from "@/lib/format";
import { Loader2, MapPin, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useDemo } from "@/context/DemoContext";
import { toast } from "sonner";

const selectCls = "bg-[#122A3F] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]";

export default function MapView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showDemo } = useDemo();
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const jitterRef = useRef({});
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geocoding, setGeocoding] = useState(false);
  const [filters, setFilters] = useState({ score_min: "", roi_min: "", stato: "", tipologia: "", tribunale: "" });
  const [zonaF, setZonaF] = useState("");
  const [raggioF, setRaggioF] = useState("");
  const [fonteF, setFonteF] = useState("");
  const [fontiOpts, setFontiOpts] = useState([]);
  useEffect(() => { api.get("/meta").then((r) => setFontiOpts(r.data.fonti || [])).catch(() => {}); }, []);

  const load = () => {
    setLoading(true);
    const params = { include_demo: showDemo };
    if (raggioF && zonaF) { params.centro = zonaF; params.raggio_km = raggioF; }
    else if (zonaF) { params.zona = zonaF; }
    if (fonteF) params.fonte = fonteF;
    api.get("/map/deals", { params }).then((r) => setDeals(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [showDemo, zonaF, raggioF, fonteF]);

  const opts = useMemo(() => ({
    stati: [...new Set(deals.map((d) => d.stato).filter(Boolean))].sort(),
    tipologie: [...new Set(deals.map((d) => d.tipologia).filter(Boolean))].sort(),
    tribunali: [...new Set(deals.map((d) => d.tribunale).filter(Boolean))].sort(),
  }), [deals]);

  const filtered = useMemo(() => deals.filter((d) => {
    if (filters.score_min && (d.score?.total ?? 0) < Number(filters.score_min)) return false;
    if (filters.roi_min && (d.financials?.roi ?? -999) < Number(filters.roi_min)) return false;
    if (filters.stato && d.stato !== filters.stato) return false;
    if (filters.tipologia && d.tipologia !== filters.tipologia) return false;
    if (filters.tribunale && d.tribunale !== filters.tribunale) return false;
    return true;
  }), [deals, filters]);

  useEffect(() => {
    if (!mapRef.current) {
      const m = L.map("rebreton-map", { center: [42.5, 12.5], zoom: 6, scrollWheelZoom: true });
      L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
        attribution: "&copy; OpenStreetMap &copy; CARTO", maxZoom: 19,
      }).addTo(m);
      mapRef.current = m;
      layerRef.current = L.layerGroup().addTo(m);
    }
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        layerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const m = mapRef.current, lg = layerRef.current;
    if (!m || !lg) return;
    lg.clearLayers();
    const pts = [];
    filtered.forEach((d) => {
      if (d.lat == null || d.lng == null) return;
      const lvl = d.priority?.level;
      const color = PRIORITY[lvl]?.color || scoreColor(d.score?.total);
      if (!jitterRef.current[d.id]) {
        jitterRef.current[d.id] = [(Math.random() - 0.5) * 0.004, (Math.random() - 0.5) * 0.004];
      }
      const [jlat, jlng] = jitterRef.current[d.id];
      const lat = d.lat + jlat, lng = d.lng + jlng;
      const marker = L.circleMarker([lat, lng], {
        radius: 8, color, weight: 2, fillColor: color, fillOpacity: 0.55,
      });
      marker.bindPopup(
        `<div style="min-width:180px">
          <strong>${d.indirizzo || d.comune || "Operazione"}</strong>${d.is_demo ? ' <span style="font-size:9px;font-weight:700;color:#EA7B2B;border:1px solid rgba(234,123,43,0.4);background:rgba(234,123,43,0.12);padding:1px 4px;border-radius:2px">DEMO</span>' : ""}<br/>
          <span style="color:#888">${d.comune || ""} ${d.provincia ? "(" + d.provincia + ")" : ""} · ${d.tipologia || ""}</span><br/>
          <span>Score <b>${d.score?.total ?? "—"}</b> · ROI <b>${d.financials?.roi ?? "—"}%</b></span><br/>
          <span>Base ${d.prezzo_base ? "€ " + Number(d.prezzo_base).toLocaleString("it-IT") : "—"} · ${d.stato || ""}</span><br/>
          <a href="/operazioni/${d.id}" style="color:#EA7B2B">Apri operazione →</a>
        </div>`
      );
      marker.on("click", () => {});
      lg.addLayer(marker);
      pts.push([lat, lng]);
    });
    if (pts.length) {
      try { m.fitBounds(pts, { padding: [40, 40], maxZoom: 12 }); } catch { /* noop */ }
    }
  }, [filtered]);

  const runGeocode = async () => {
    setGeocoding(true);
    try {
      const { data } = await api.post("/geocode/run");
      toast.success(`Geocodifica: ${data.geocoded} nuove posizioni`);
      load();
    } catch { toast.error("Errore geocodifica"); }
    finally { setGeocoding(false); }
  };

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6" data-testid="map-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-light text-white">Mappa opportunità</h1>
          <p className="text-sm text-zinc-500 mt-1">{filtered.length} di {deals.length} operazioni geolocalizzate · cluster di opportunità</p>
        </div>
        {(user?.role === "admin" || user?.role === "analista") && (
          <button onClick={runGeocode} disabled={geocoding} data-testid="geocode-btn" className="flex items-center gap-2 border border-white/10 text-zinc-300 text-sm px-4 py-2.5 rounded-sm hover:bg-white/5 transition-colors disabled:opacity-50">
            {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Aggiorna posizioni
          </button>
        )}
      </div>

      <div className="bg-[#122A3F] border border-white/10 rounded-sm p-4 flex flex-wrap items-center gap-3" data-testid="map-filters">
        <div className="flex items-center gap-2 bg-[#0A1826] border border-white/10 rounded-sm px-3 min-w-[180px]">
          <MapPin className="w-4 h-4 text-[#EA7B2B]" />
          <input value={zonaF} onChange={(e) => setZonaF(e.target.value)} placeholder="Zona (es. Roma, MI…)" data-testid="map-zona" className="bg-transparent py-2 text-sm text-white focus:outline-none flex-1 w-full" />
        </div>
        <select value={raggioF} onChange={(e) => setRaggioF(e.target.value)} data-testid="map-raggio" title="Cerca entro un raggio dalla Zona indicata" className={selectCls}>
          <option value="">Raggio: —</option>
          <option value="10">entro 10 km</option>
          <option value="25">entro 25 km</option>
          <option value="50">entro 50 km</option>
          <option value="100">entro 100 km</option>
        </select>
        <select value={fonteF} onChange={(e) => setFonteF(e.target.value)} data-testid="map-filter-fonte" className={selectCls}>
          <option value="">Tutte le fonti</option>
          {fontiOpts.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <select value={filters.stato} onChange={(e) => set("stato", e.target.value)} data-testid="map-filter-stato" className={selectCls}>
          <option value="">Tutti gli stati</option>
          {opts.stati.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.tipologia} onChange={(e) => set("tipologia", e.target.value)} data-testid="map-filter-tipologia" className={selectCls}>
          <option value="">Tutte le tipologie</option>
          {opts.tipologie.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.tribunale} onChange={(e) => set("tribunale", e.target.value)} data-testid="map-filter-tribunale" className={selectCls}>
          <option value="">Tutti i tribunali</option>
          {opts.tribunali.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.score_min} onChange={(e) => set("score_min", e.target.value)} data-testid="map-filter-score" className={selectCls}>
          <option value="">Score min</option>
          {[40, 50, 60, 70, 80].map((s) => <option key={s} value={s}>Score ≥ {s}</option>)}
        </select>
        <select value={filters.roi_min} onChange={(e) => set("roi_min", e.target.value)} data-testid="map-filter-roi" className={selectCls}>
          <option value="">ROI min</option>
          {[0, 10, 20, 30].map((s) => <option key={s} value={s}>ROI ≥ {s}%</option>)}
        </select>
        <div className="flex items-center gap-3 ml-auto text-xs">
          {["alta", "media", "bassa"].map((lv) => (
            <span key={lv} className="flex items-center gap-1.5 text-zinc-400"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PRIORITY[lv].color }} />{PRIORITY[lv].short}</span>
          ))}
        </div>
      </div>

      <div className="relative rounded-sm overflow-hidden border border-white/10" style={{ height: "600px" }}>
        {loading && <div className="absolute inset-0 z-[500] flex items-center justify-center bg-[#0A1826]/60"><Loader2 className="w-8 h-8 text-[#EA7B2B] animate-spin" /></div>}
        {!loading && deals.length === 0 && (
          <div className="absolute inset-0 z-[500] flex flex-col items-center justify-center gap-3 bg-[#0A1826]/70 text-zinc-400">
            <MapPin className="w-8 h-8" /> Nessuna operazione geolocalizzata. Premi "Aggiorna posizioni".
          </div>
        )}
        <div id="rebreton-map" style={{ height: "100%", width: "100%" }} />
      </div>
    </div>
  );
}
